import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { setToken } from '@/api/axiosClient'
import usersApi from '@/api/identity/usersApi'
import { TOKEN_KEY } from '@/constant/common'
import { adminRoles, userRoles } from '@/constant/role'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import { ADMIN_ROUTES } from '@/features/admin/routes'
import { useAuthContext } from '@/features/auth/context'
import { signinCallback } from '@/features/auth/oidcClient'
import { setUser } from '@/store/slices/user/userSlice'
import { useAppDispatch } from '@/store/stores'

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 71, 68, 0.55), 0 0 32px rgba(0, 71, 68, 0.45); }
  50% { box-shadow: 0 0 0 12px rgba(0, 71, 68, 0), 0 0 48px rgba(0, 71, 68, 0.7); }
`

const fullscreenSx = {
  position: 'fixed' as const,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#002031',
  backgroundImage:
    'radial-gradient(circle at 18% 22%, rgba(0, 71, 68, 0.4) 0%, transparent 55%), radial-gradient(circle at 82% 78%, rgba(0, 96, 92, 0.3) 0%, transparent 60%)',
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 100,
}

const OidcCallback = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { setIsLogin } = useAuthContext()
  const processed = useRef(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (processed.current) return
    processed.current = true

    ;(async () => {
      try {
        const oidcUser = await signinCallback()

        localStorage.setItem(TOKEN_KEY, oidcUser.access_token)
        setToken(oidcUser.access_token)

        const { data: user } = await usersApi.getMe()
        dispatch(setUser(user))

        setIsLogin(true)

        let target = '/'
        if (user?.email === 'rodney_mckeown@sentosagolf.com') {
          target = user?.gmtTimeZone === null
            ? CONFIG_SETUP_ROUTES.index.to
            : OPERATIONS_ROUTES.sensors.to
        } else if (adminRoles.includes(user?.role)) {
          target = user?.gmtTimeZone === null
            ? CONFIG_SETUP_ROUTES.index.to
            : ADMIN_ROUTES.index.to
        } else if (userRoles.includes(user?.role)) {
          target = user?.gmtTimeZone === null
            ? CONFIG_SETUP_ROUTES.index.to
            : OPERATIONS_ROUTES.index.to
        }

        // Redirect as soon as the user is resolved — no artificial wait.
        navigate(target, { replace: true })
      } catch (error) {
        console.error('OIDC callback failed:', error)
        setError('SSO login failed. Please try again.')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <Box sx={fullscreenSx}>
        <Stack alignItems="center" spacing={3} sx={{ padding: '24px', maxWidth: '500px' }}>
          <Typography sx={{ color: '#C9D1D9', fontSize: '14px', textAlign: 'center' }}>
            {error}
          </Typography>
          <Button
            variant="primary"
            onClick={() => navigate('/', { replace: true })}
            sx={{ minWidth: '120px', height: '40px', borderRadius: '8px' }}
          >
            Back to Login
          </Button>
        </Stack>
      </Box>
    )
  }

  // Transient transition shown only while the callback is being processed.
  // navigate() above fires the moment the user is fetched, so this is a brief
  // visual handoff, not a timed wait.
  return (
    <Box sx={fullscreenSx}>
      <Stack alignItems="center" spacing={3}>
        <Box
          sx={{
            position: 'relative',
            width: '96px',
            height: '96px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress
            size={96}
            thickness={2.2}
            sx={{
              color: '#00928C',
              position: 'absolute',
              inset: 0,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <CircularProgress
            size={96}
            thickness={2.2}
            variant="determinate"
            value={100}
            sx={{
              color: 'rgba(255, 255, 255, 0.06)',
              position: 'absolute',
              inset: 0,
            }}
          />
          <Box
            sx={{
              width: '62px',
              height: '62px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #004744 0%, #006A66 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${pulseGlow} 1.8s ease-in-out infinite`,
            }}
          >
            <CircularProgress size={24} thickness={4} sx={{ color: '#fff' }} />
          </Box>
        </Box>

        <Stack alignItems="center" spacing={0.5}>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '18px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            SSO login successful
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '13px',
              lineHeight: 1.5,
              textAlign: 'center',
              maxWidth: '340px',
            }}
          >
            Redirecting…
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default OidcCallback
